// src/components/Games/CheckTheBalance.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './CheckTheBalance.css';

const API_ENDPOINT = '/api/balance';

// CHANGED: corruption tuning — how much each heaven.exe reset degrades metrics.
// Only the two R40 components (yoyGrowth + fcfMargin) plus a couple of
// supporting metrics are touched, so the composite score visibly slips.
const CORRUPTION_KEY = 'heaven_exe_corruption'; // sessionStorage counter
const CORRUPTION_MAX = 6;                        // cap so it can't nuke to zero
const CORRUPTION_PENALTY = {
  yoyGrowth:   4,   // −4 pts YoY growth per reset  (hits R40 directly)
  fcfMargin:   2,   // −2 pts FCF margin per reset  (hits R40 directly)
  qoqGrowth:   1,   // small collateral damage
  nrr:         1,   // small collateral damage
};

// ─── Seed / fallback data (OpenAI-realistic) ──────────────────────────────────
const FALLBACK_DATA = {
  metrics: {
    yoyGrowth:   { actual: 150,  target: 100 },
    qoqGrowth:   { actual: 28,   target: 20  },
    grossMargin: { actual: 74,   target: 70  },
    fcfMargin:   { actual: -28,  target: -10 },
    nrr:         { actual: 120,  target: 110 },
    magicNumber: { actual: 1.4,  target: 1.0 },
    cashRunway:  { actual: 18,   target: 24  },
  },
  lastUpdated: new Date().toISOString(),
};

// ─── Metric config — labels, units, R40 role, benchmark context ───────────────
const METRIC_CONFIG = {
  yoyGrowth: {
    label:        'YoY REVENUE GROWTH',
    unit:         '%',
    r40:          true,
    higherBetter: true,
    benchmark:    'R40 COMPONENT',
    ascii:        ['▲ GROWTH', '  VECTOR '],
  },
  qoqGrowth: {
    label:        'QoQ REVENUE GROWTH',
    unit:         '%',
    r40:          false,
    higherBetter: true,
    benchmark:    'QUARTERLY PULSE',
    ascii:        ['▲ QUARTER', '   DELTA  '],
  },
  grossMargin: {
    label:        'GROSS MARGIN',
    unit:         '%',
    r40:          false,
    higherBetter: true,
    benchmark:    'UNIT ECONOMICS',
    ascii:        ['◈ MARGIN', '  LAYER  '],
  },
  fcfMargin: {
    label:        'FCF MARGIN',
    unit:         '%',
    r40:          true,
    higherBetter: true,
    benchmark:    'R40 COMPONENT',
    ascii:        ['◆ CASH', ' FLOW  '],
  },
  nrr: {
    label:        'NET REVENUE RETENTION',
    unit:         '%',
    r40:          false,
    higherBetter: true,
    benchmark:    'EXPANSION SIGNAL',
    ascii:        ['⟳ RETAIN', '  INDEX  '],
  },
  magicNumber: {
    label:        'MAGIC NUMBER',
    unit:         'x',
    r40:          false,
    higherBetter: true,
    benchmark:    'CAC EFFICIENCY',
    ascii:        ['✦ MAGIC', '   NUM  '],
  },
  cashRunway: {
    label:        'CASH RUNWAY',
    unit:         ' MO',
    r40:          false,
    higherBetter: true,
    benchmark:    'SURVIVAL WINDOW',
    ascii:        ['◉ RUNWAY', '  CLOCK  '],
  },
};

// ─── Boot sequence lines ───────────────────────────────────────────────────────
const BOOT_SEQUENCE = [
  { text: '',                                               delay: 200 },
  { text: '▓▓▓ RULE OF 40 PROTOCOL — INITIALIZING ▓▓▓',   delay: 500 },
  { text: '',                                               delay: 150 },
  { text: 'Loading financial data.....................',  delay: 300 },
  { text: 'Calibrating growth vectors.....................',   delay: 280 },
  { text: 'Fetching FCF margin data.......................',   delay: 320 },
  { text: 'Cross-referencing benchmark targets...........',   delay: 290 },
  { text: 'Computing Rule of 40 composite score.........',    delay: 350 },
  { text: 'Validating capital efficiency metrics.........',   delay: 300 },
  { text: '',                                               delay: 150 },
  { text: '▓ DATA LOADED — RENDERING DASHBOARD ▓',    delay: 500 },
  { text: '',                                               delay: 200 },
];

// CHANGED: read the current corruption count (0..CORRUPTION_MAX) from session.
function getCorruptionCount() {
  const raw = parseInt(localStorage.getItem(CORRUPTION_KEY) || '0', 10);
  if (Number.isNaN(raw) || raw < 0) return 0;
  return Math.min(raw, CORRUPTION_MAX);
}

// CHANGED: apply the corruption penalty to a metrics object, returning a NEW
// object (never mutates the fallback/source). Targets are left untouched so the
// deltas visibly widen — actuals slip further below target on each reset.
function applyCorruption(metrics, count) {
  if (count <= 0) return metrics;
  const degraded = {};
  Object.keys(metrics).forEach((key) => {
    const m = metrics[key];
    const penaltyPerReset = CORRUPTION_PENALTY[key] || 0;
    if (penaltyPerReset > 0) {
      degraded[key] = {
        actual: Math.round((m.actual - penaltyPerReset * count) * 10) / 10,
        target: m.target,
      };
    } else {
      degraded[key] = { ...m };
    }
  });
  return degraded;
}

// ─── Rule of 40 helpers ───────────────────────────────────────────────────────
function calcR40(metrics) {
  const growth = metrics?.yoyGrowth?.actual ?? 0;
  const fcf    = metrics?.fcfMargin?.actual ?? 0;
  return Math.round((growth + fcf) * 10) / 10;
}

function calcR40Target(metrics) {
  const growth = metrics?.yoyGrowth?.target ?? 0;
  const fcf    = metrics?.fcfMargin?.target ?? 0;
  return Math.round((growth + fcf) * 10) / 10;
}

function r40Color(score) {
  if (score >= 60) return '#00ff41';
  if (score >= 40) return '#aaff44';
  if (score >= 20) return '#ffaa00';
  return '#ff0040';
}

function r40Label(score) {
  if (score >= 60) return 'ELITE PERFORMER';
  if (score >= 40) return 'RULE OF 40: PASS';
  if (score >= 20) return 'BELOW THRESHOLD';
  return 'CRITICAL — RESTRUCTURE';
}

// ─── Delta badge ──────────────────────────────────────────────────────────────
function getDelta(actual, target, higherBetter) {
  const diff    = actual - target;
  const sign    = diff >= 0 ? '+' : '';
  const beating = higherBetter ? diff >= 0 : diff <= 0;
  return {
    text: `${sign}${Math.round(diff * 10) / 10}`,
    color: beating ? '#00ff41' : '#ff0040',
    beating,
  };
}

// ─── ASCII mini bar ───────────────────────────────────────────────────────────
function AsciiBar({ actual, target, unit, higherBetter, color }) {
  const WIDTH  = 20;
  const maxVal = Math.max(Math.abs(actual), Math.abs(target), 1);
  const fill   = Math.min(WIDTH, Math.max(0, Math.round((Math.abs(actual) / maxVal) * WIDTH)));
  const tPos   = Math.min(WIDTH - 1, Math.max(0, Math.round((Math.abs(target) / maxVal) * WIDTH)));

  const bar = Array.from({ length: WIDTH }, (_, i) => {
    if (i === tPos) return '┼';
    if (i < fill)   return '█';
    return '░';
  }).join('');

  const delta = getDelta(actual, target, higherBetter);

  return (
    <div className="r40-bar-row">
      <span className="r40-bar-track" style={{ color }}>
        [{bar}]
      </span>
      <span className="r40-bar-delta" style={{ color: delta.color }}>
        {delta.text}{unit}
      </span>
    </div>
  );
}

// ─── Single metric card ───────────────────────────────────────────────────────
function MetricCard({ metricKey, actual, target }) {
  const cfg             = METRIC_CONFIG[metricKey];
  const delta           = getDelta(actual, target, cfg.higherBetter);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 2000);
    return () => clearInterval(id);
  }, []);

  const color    = delta.beating ? '#00ff41' : '#ff0040';
  const dimColor = delta.beating ? '#00ff4166' : '#ff004066';

  const fmtVal = (v) => {
    if (metricKey === 'magicNumber') return v.toFixed(2);
    return v % 1 === 0 ? v : v.toFixed(1);
  };

  return (
    <div className="r40-card" style={{ borderColor: dimColor }}>
      <div className="r40-card-header">
        <span className="r40-card-label" style={{ color: `${color}cc` }}>
          {cfg.label}
        </span>
        <span
          className="r40-card-benchmark"
          style={{ color: cfg.r40 ? '#ffaa0088' : '#33333388' }}
        >
          {cfg.benchmark}
        </span>
      </div>

      <div className="r40-card-value-row">
        <div
          className="r40-card-actual"
          style={{ color, textShadow: `0 0 12px ${color}55` }}
        >
          {fmtVal(actual)}{cfg.unit}
        </div>

        <div className="r40-card-vs-block">
          <div className="r40-card-vs-label">TARGET</div>
          <div className="r40-card-target" style={{ color: '#ffaa00' }}>
            {fmtVal(target)}{cfg.unit}
          </div>
          <div className="r40-card-delta" style={{ color: delta.color }}>
            {delta.text}{cfg.unit}
          </div>
        </div>

        <pre
          className="r40-card-ascii"
          style={{ color: tick % 2 === 0 ? `${color}99` : `${color}44` }}
        >
          {cfg.ascii.join('\n')}
        </pre>
      </div>

      <AsciiBar
        actual={actual}
        target={target}
        unit={cfg.unit}
        higherBetter={cfg.higherBetter}
        color={color}
      />

      <div className="r40-card-status" style={{ color: delta.color }}>
        {delta.beating ? '▓ ON TARGET ▓' : '▓ BELOW TARGET ▓'}
      </div>
    </div>
  );
}

// ─── R40 Hero ─────────────────────────────────────────────────────────────────
function R40Hero({ score, targetScore, glitch }) {
  const color       = r40Color(score);
  const targetColor = r40Color(targetScore);
  const delta       = getDelta(score, targetScore, true);

  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    if (score === 0) return;
    let current = 0;
    const step  = score / 40;
    const id    = setInterval(() => {
      current += step;
      if (current >= score) { setDisplayed(score); clearInterval(id); return; }
      setDisplayed(Math.round(current));
    }, 30);
    return () => clearInterval(id);
  }, [score]);

  const GAUGE_W = 11;
  const min     = -20, max = 100;
  const norm    = Math.min(
    GAUGE_W,
    Math.max(0, Math.round(((score - min) / (max - min)) * GAUGE_W))
  );
  const gauge = Array.from({ length: GAUGE_W }, (_, i) => i < norm ? '█' : '░').join('');

  return (
    <div className={`r40-hero ${glitch ? 'r40-glitch' : ''}`}>
      <div className="r40-hero-label">▓▓▓ RULE OF 40 COMPOSITE SCORE ▓▓▓</div>

      <div className="r40-hero-score-row">
        <div
          className="r40-hero-score"
          style={{ color, textShadow: `0 0 20px ${color}66` }}
        >
          {displayed >= 0 ? '+' : ''}{displayed}
        </div>
        <div className="r40-hero-side">
          <div className="r40-hero-formula">YoY GROWTH + FCF MARGIN = R40</div>
          <div className="r40-hero-target" style={{ color: targetColor }}>
            TARGET: {targetScore >= 0 ? '+' : ''}{targetScore}
          </div>
          <div className="r40-hero-delta" style={{ color: delta.color }}>
            DELTA: {delta.text}
          </div>
        </div>
      </div>

      <div className="r40-hero-gauge">
        <span className="r40-hero-gauge-label" style={{ color: '#ff004088' }}>-∞</span>
        <span className="r40-hero-gauge-track" style={{ color }}>
          [{gauge}]
        </span>
        <span className="r40-hero-gauge-label" style={{ color: '#00ff4188' }}>+∞</span>
        <span className="r40-hero-gauge-marker" style={{ color: '#ffaa00' }}>
          ┼ TARGET
        </span>
      </div>

      <div className="r40-hero-verdict" style={{ color }}>
        {r40Label(score)}
      </div>

      <div className="r40-hero-sublabel">
        YoY GROWTH & FCF MARGIN — COMBINED HEALTH INDEX
      </div>
    </div>
  );
}

// ─── Scrolling ticker ─────────────────────────────────────────────────────────
function DataTicker({ metrics }) {
  const items = Object.entries(metrics).map(([key, { actual, target }]) => {
    const cfg   = METRIC_CONFIG[key];
    const delta = getDelta(actual, target, cfg.higherBetter);
    const sign  = actual >= 0 ? '+' : '';
    return (
      `${cfg.label}: ${sign}${actual}${cfg.unit}  ` +
      `[TGT:${target}${cfg.unit}  Δ:${delta.text}${cfg.unit}]`
    );
  });

  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex(i => (i + 1) % items.length), 4000);
    return () => clearInterval(id);
  }, [items.length]);

  return (
    <div className="r40-ticker">
      <span className="r40-ticker-prefix">[LIVE DATA] </span>
      <span className="r40-ticker-text" key={index}>{items[index]}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function CheckTheBalance() {
  const [data,         setData]         = useState(null);
  const [bootComplete, setBootComplete] = useState(false);
  const [bootLines,    setBootLines]    = useState([]);
  const [glitch,       setGlitch]       = useState(false);

  // CHANGED: track corruption level in React state so a reset re-renders the
  // dashboard with degraded values immediately (no page reload needed).
  const [corruption, setCorruption] = useState(getCorruptionCount());

  // CHANGED: keep the last *clean* metrics (pre-corruption) in a ref so we can
  // re-derive degraded values whenever the corruption count changes.
  const cleanMetricsRef = useRef(FALLBACK_DATA.metrics);

  // ── Fetch — normalise response, then apply session corruption ──────────────
  const fetchBalance = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINT);
      if (!res.ok) throw new Error(`${res.status}`);
      const json = await res.json();

      if (!json.metrics || typeof json.metrics !== 'object') {
        console.warn('[R40] API returned legacy/missing shape — using fallback data');
        cleanMetricsRef.current = FALLBACK_DATA.metrics;         // CHANGED
        const count = getCorruptionCount();                      // CHANGED
        setData({
          metrics: applyCorruption(FALLBACK_DATA.metrics, count), // CHANGED
          lastUpdated: new Date().toISOString(),
        });
        return;
      }

      const safeMetrics = { ...FALLBACK_DATA.metrics };
      Object.keys(FALLBACK_DATA.metrics).forEach(key => {
        if (
          json.metrics[key] &&
          typeof json.metrics[key].actual === 'number' &&
          typeof json.metrics[key].target === 'number'
        ) {
          safeMetrics[key] = json.metrics[key];
        }
      });

      cleanMetricsRef.current = safeMetrics;                     // CHANGED
      const count = getCorruptionCount();                        // CHANGED

      setData({
        // CHANGED: degrade the clean metrics by the current corruption count
        metrics:     applyCorruption(safeMetrics, count),
        lastUpdated: json.lastUpdated ?? new Date().toISOString(),
      });
    } catch (err) {
      console.error('[R40] fetch failed:', err);
      cleanMetricsRef.current = FALLBACK_DATA.metrics;           // CHANGED
      const count = getCorruptionCount();                        // CHANGED
      setData({
        metrics: applyCorruption(FALLBACK_DATA.metrics, count),  // CHANGED
        lastUpdated: new Date().toISOString(),
      });
    }
  }, []);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  useEffect(() => {
    const id = setInterval(fetchBalance, 60000);
    return () => clearInterval(id);
  }, [fetchBalance]);

  // CHANGED: listen for the heaven.exe reset event fired by BookOfEnoch.
  // Each reset increments the corruption counter (capped), persists it to
  // sessionStorage, triggers a glitch, and re-derives degraded metrics.
  useEffect(() => {
    const handleReset = () => {
      setCorruption(prev => {
        const next = Math.min(prev + 1, CORRUPTION_MAX);
        localStorage.setItem(CORRUPTION_KEY, String(next));
        return next;
      });
      setGlitch(true);
      setTimeout(() => setGlitch(false), 400);
    };
    window.addEventListener('heaven-exe-reset', handleReset);
    return () => window.removeEventListener('heaven-exe-reset', handleReset);
  }, []);

  useEffect(() => {
    const handleHeal = () => {
      localStorage.removeItem(CORRUPTION_KEY);
      setCorruption(0);
      setGlitch(true);
      setTimeout(() => setGlitch(false), 400);
    };
    window.addEventListener('heaven-exe-alive', handleHeal);
    return () => window.removeEventListener('heaven-exe-alive', handleHeal);
  }, []);

  

  // CHANGED: whenever corruption changes, re-derive the displayed metrics from
  // the last clean snapshot. This makes the degradation appear instantly and
  // stay consistent with subsequent fetches.
  useEffect(() => {
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        metrics: applyCorruption(cleanMetricsRef.current, corruption),
      };
    });
  }, [corruption]);

  // ── Boot sequence ─────────────────────────────────────────────────────────
  useEffect(() => {
    let total = 0;
    const timeouts = [];
    BOOT_SEQUENCE.forEach(line => {
      total += line.delay;
      timeouts.push(setTimeout(() => {
        setBootLines(prev => [...prev, line]);
      }, total));
    });
    timeouts.push(setTimeout(() => setBootComplete(true), total + 400));
    return () => timeouts.forEach(clearTimeout);
  }, []);

  // ── Random glitch ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!bootComplete) return;
    const id = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 150);
    }, 12000 + Math.random() * 10000);
    return () => clearInterval(id);
  }, [bootComplete]);

  const fmtDate = (iso) => {
    if (!iso) return 'UNKNOWN';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`r40-container ${glitch ? 'r40-glitch' : ''}`}>

      {/* Boot */}
      {!bootComplete && (
        <div className="r40-boot">
          {bootLines.map((line, i) => (
            <div key={i} className="r40-boot-line">
              {line.text ? (
                <>
                  <span className="r40-boot-prefix">[SYS] </span>
                  <span className="r40-boot-text">{line.text}</span>
                </>
              ) : (
                <div style={{ height: '0.8em' }} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Main dashboard */}
      {bootComplete && data && (
        <div className="r40-main">

          <div className="r40-title-block">
            <div className="r40-title">▓▓▓ RULE OF 40 - THE IPO NEEDS TO BE A SUCCESS! ▓▓▓</div>
            <div className="r40-subtitle">
              FINANCIAL HEALTH — ACTUALS VS TARGETS
            </div>
          </div>

          {/* CHANGED: corruption banner — only shown once heaven.exe was reset */}
          {corruption > 0 && (
            <div className="r40-corruption-banner">
              ▓ SIGNAL CORRUPTED — HEAVEN.EXE INTEGRITY FAULT ×{corruption} ▓
              <span className="r40-corruption-sub">
                NEURAL SUBSTRATE DEGRADED — METRICS RECALCULATED DOWNWARD
              </span>
            </div>
          )}

          <R40Hero
            score={calcR40(data.metrics)}
            targetScore={calcR40Target(data.metrics)}
            glitch={glitch}
          />

          {data.metrics && (
            <>
              <DataTicker metrics={data.metrics} />

              <div className="r40-grid">
                {Object.entries(data.metrics).map(([key, val]) => {
                  if (!METRIC_CONFIG[key])                  return null;
                  if (typeof val?.actual !== 'number')      return null;
                  if (typeof val?.target !== 'number')      return null;
                  return (
                    <MetricCard
                      key={key}
                      metricKey={key}
                      actual={val.actual}
                      target={val.target}
                    />
                  );
                })}
              </div>
            </>
          )}

          <div className="r40-footer">
            <span>LAST UPDATE: {fmtDate(data.lastUpdated)}</span>
            <span>
              R40 = YoY GROWTH RATE + FCF MARGIN  |  THRESHOLD: 40
            </span>
            <span className="r40-footer-warn">
              PAST PERFORMANCE DOES NOT GUARANTEE FUTURE DOMINATION
            </span>
          </div>

        </div>
      )}

      {/* Loading */}
      {bootComplete && !data && (
        <div className="r40-loading">
          LOADING FINANCIAL DATA<span className="bbs-loading-dots" />
        </div>
      )}

    </div>
  );
}